import { useEffect, useMemo, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../firebase-client';
import SubmitButton from '../../components/forms/formComponents/SubmitButton';
import ErrorMessage from '../../components/forms/formComponents/ErrorMessage';
import { getCurrencyMeta } from '../../utils/currency';
import '../../styles/moorings.css';

function statusLabel(value) {
  const text = String(value || '').replace(/_/g, ' ');
  return text ? text.charAt(0).toUpperCase() + text.slice(1) : 'Unknown';
}

export default function MooringPaymentPage() {
  const { linkId } = useParams();
  const [searchParams] = useSearchParams();
  const teamId = searchParams.get('teamId') || '';
  const [view, setView] = useState(null);
  const [selectedCadence, setSelectedCadence] = useState('');
  const [mode, setMode] = useState('subscription');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError('');
      try {
        const fn = httpsCallable(functions, 'claimMooringPaymentLink');
        const result = await fn({ linkId, teamId });
        if (cancelled) return;
        const safeView = result.data?.view || null;
        setView(safeView);
        setSelectedCadence(safeView?.paymentOptions?.[0]?.cadence || '');
      } catch (err) {
        if (!cancelled) setError(err?.message || 'Could not open this mooring payment link.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    if (linkId) load();
    return () => {
      cancelled = true;
    };
  }, [linkId, teamId]);

  const currency = view?.currency || 'GBP';
  const { symbol, decimals } = getCurrencyMeta(currency);
  const formatMoney = (minor) => `${symbol}${(Number(minor || 0) / Math.pow(10, decimals)).toFixed(decimals)}`;
  const selectedOption = useMemo(() => (
    view?.paymentOptions?.find((option) => option.cadence === selectedCadence) || view?.paymentOptions?.[0] || null
  ), [selectedCadence, view]);
  const checkoutStatus = searchParams.get('checkout');

  const startCheckout = async (event) => {
    event.preventDefault();
    if (!selectedOption) return;
    setSubmitting(true);
    setError('');
    try {
      const fn = httpsCallable(functions, mode === 'subscription' ? 'createMooringSubscription' : 'createMooringCheckoutSession');
      const result = await fn({ linkId, teamId: view?.teamId || teamId, cadence: selectedOption.cadence });
      if (!result.data?.url) throw new Error('No checkout URL returned.');
      window.location.href = result.data.url;
    } catch (err) {
      setError(err?.message || 'Could not start mooring checkout.');
      setSubmitting(false);
    }
  };

  return (
    <div className="page mooring-payment-page">
      <section className="section">
        <div className="mooring-payment-card">
          <div className="mooring-payment-card__header">
            <div>
              <p className="moorings-card__eyebrow">Mooring payment</p>
              <h1>{view?.teamName || 'Mooring fee'}</h1>
            </div>
            <Link className="btn btn--secondary btn--sm" to="/dashboard">Dashboard</Link>
          </div>

          {loading && <p className="text-muted">Loading mooring payment…</p>}
          {error && <ErrorMessage message={error} />}
          {checkoutStatus === 'success' && (
            <div className="moorings-payment-preview">
              <span>Payment received. The club record may take a moment to update.</span>
              {(view?.teamId || teamId) && <Link className="btn btn--primary btn--sm" to={`/teams/${view?.teamId || teamId}`}>Back to club</Link>}
            </div>
          )}
          {checkoutStatus === 'cancelled' && <p className="text-muted">Checkout was cancelled. No money has been taken.</p>}

          {view && !loading && checkoutStatus !== 'success' && (
            <>
              <dl className="mooring-payment-summary">
                <div><dt>Space</dt><dd>{[view.areaName, view.spaceLabel].filter(Boolean).join(' · ') || 'Mooring space'}</dd></div>
                <div><dt>Vessel</dt><dd>{[view.vesselName, view.vesselType].filter(Boolean).join(' · ') || 'Not recorded'}</dd></div>
                {view.boatClassName && <div><dt>Boat class</dt><dd>{view.boatClassName}</dd></div>}
                <div><dt>Payer</dt><dd>{view.payerType === 'non_member' ? 'Non-member customer' : 'Club member'} · {view.payerEmail}</dd></div>
                <div><dt>Status</dt><dd>{statusLabel(view.paymentStatus)}</dd></div>
              </dl>

              <form onSubmit={startCheckout} className="modal-form">
                {view.paymentOptions?.length > 1 && (
                  <label>Payment cadence<select className="input" value={selectedCadence} onChange={(event) => setSelectedCadence(event.target.value)}>
                    {view.paymentOptions.map((option) => (
                      <option key={option.cadence} value={option.cadence}>{option.cadence} · {formatMoney(option.amount)}</option>
                    ))}
                  </select></label>
                )}
                {selectedOption && (
                  <div className="moorings-payment-preview">
                    <span>{selectedOption.cadence}: {formatMoney(selectedOption.amount)}</span>
                    <span>Price source: {selectedOption.source === 'class' ? `Boat class${selectedOption.boatClassName ? ` · ${selectedOption.boatClassName}` : ''}` : selectedOption.source}</span>
                    <span>{view.stripeFeeHandling === 'add' ? 'Stripe fees added at checkout' : 'Stripe fees deducted from club payout'}</span>
                  </div>
                )}
                <label>Payment mode<select className="input" value={mode} onChange={(event) => setMode(event.target.value)}>
                  <option value="subscription">Recurring payment</option>
                  <option value="manual">Pay one period upfront</option>
                </select></label>
                <div className="form-actions">
                  <SubmitButton type="submit" variant="primary" label={submitting ? 'Opening checkout…' : 'Continue to payment'} disabled={submitting || !selectedOption} />
                </div>
              </form>
            </>
          )}
        </div>
      </section>
    </div>
  );
}
